import { isDateAfter, parseDate } from '@travelata-libs/utils';
import { getHotelUrl, getSingleHotelId } from './getHotelUrl';
import { getToursUrl } from './getToursUrl';
import { toTouristRooms } from './touristRooms';
import type { PartialSearchCriteria, SearchCriteriaHistory } from '../model/types';
import { getMinPricesByCriterias } from '@/shared/api/getMinPricesByCriterias';
import { useTravelataAppConfig } from '@/shared/composables';
import { PagesUrl } from '@/shared/config/pages';

const STORAGE_KEY = 'tr_historySearch';

function isCompleteRecord(record: Partial<SearchCriteriaHistory> | null): record is SearchCriteriaHistory {
  return Boolean(
    record?.countries?.length &&
      record.departureCity &&
      record.checkInDateRange?.from &&
      record.checkInDateRange.to &&
      record.nightRange?.from &&
      record.nightRange.to &&
      record.touristGroup?.adults,
  );
}

function readHistory(): SearchCriteriaHistory[] {
  if (typeof localStorage === 'undefined') {
    return [];
  }
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((record) => isCompleteRecord(record)) : [];
  } catch {
    return [];
  }
}

const getDestination = (
  country: number,
  resorts: number[] | undefined,
  hotels: number[] | undefined,
): PartialSearchCriteria['destination'] => {
  if (resorts) {
    return {
      kind: 'resorts',
      country: { id: country },
      resorts: resorts.map((id) => ({ id })),
    };
  } else if (hotels) {
    return {
      kind: 'hotels',
      country: { id: country },
      hotels: hotels.map((id) => ({ id })),
    };
  }

  return {
    kind: 'country',
    country: { id: country },
  };
};

export default async function getHistorySearch(withPrices?: boolean): Promise<SearchCriteriaHistory[]> {
  const currentDate = new Date();
  const appConfig = useTravelataAppConfig();
  const history = readHistory().map((h) => {
    const country = h.countries[0];
    const resorts = h.resorts;
    const hotels = h.hotels;

    const criteria: PartialSearchCriteria = {
      fromCity: { id: h?.departureCity?.id || 0 },
      tourists: { rooms: toTouristRooms(h.touristGroup) },
      nights: { from: h.nightRange?.from, to: h.nightRange?.to },
      destination: getDestination(country, resorts, hotels),
      dateRange: { from: h.checkInDateRange?.from, to: h.checkInDateRange?.to },
    };
    const hotelId = getSingleHotelId(criteria.destination);

    return {
      ...h,
      url: hotelId
        ? `${appConfig.baseHost}/${PagesUrl.GotoHotel}/${hotelId}#?${getHotelUrl(criteria)}`
        : `${appConfig.baseHost}/${PagesUrl.SerpTours}#?${getToursUrl(criteria)}`,
    };
  });

  const getParsedDate = (value: string) => {
    return parseDate(value) || new Date();
  };

  const isActual = (record: SearchCriteriaHistory) => {
    return isDateAfter(getParsedDate(record.checkInDateRange.from), currentDate);
  };

  const priceable = withPrices ? history.filter((record) => !record.touristGroup.touristsByRooms?.length) : [];

  if (!priceable.length) {
    return history.filter((record) => isActual(record));
  }

  const pricesByIndex = await getMinPricesByCriterias(priceable);
  const pricesByRecord = new Map(priceable.map((record, index) => [record, pricesByIndex[index]]));

  return history.map((item) => ({ ...item, price: pricesByRecord.get(item) })).filter((record) => isActual(record));
}
